import { FC } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Check, Flex, Regular, SecondaryButton } from '@zazume/zzm-base';
import { DateVO } from '@zazume/zzm-utils';

import { useI18n } from '#/hooks/useI18n';
import { BillingDataValidatedFrom } from '#/models/BillingData';

export interface ValidatedFiscalInformationDisplayProps {
  validatedAt: Date;
  validatedFrom?: BillingDataValidatedFrom;
}

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
`;

const ManualButtonDisabled = styled(SecondaryButton)(
  ({ theme }) => css`
    cursor: not-allowed;

    &:disabled {
      border-color: ${theme.colors.Gray500};
      color: ${theme.colors.Gray500};

      path {
        stroke: ${theme.colors.Gray500};
      }
    }
  `
);

const Container = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: ${theme.spacing.s16};

    ${theme.mq.md} {
      grid-template-columns: 1fr auto;
    }
  `
);

export const ValidatedFiscalInformationDisplay: FC<ValidatedFiscalInformationDisplayProps> = ({
  validatedAt,
  validatedFrom
}) => {
  const { t, languageVO } = useI18n();

  const isValidatedManually = validatedFrom === 'Manual';
  const formattedDate = DateVO.fromPrimitive(validatedAt).format('date', languageVO.toPlatform());
  const buttonStatus = isValidatedManually ? 'default' : 'confirm';
  const Button = isValidatedManually ? ManualButtonDisabled : SecondaryButton;

  return (
    <Container>
      <Flex direction="column" gap="s4">
        <Regular variant="S" color="Gray500">
          {t('fiscalInformationValidator.description')}
        </Regular>
        <Regular variant="S" color="GreenDark1">
          {isValidatedManually
            ? t('fiscalInformationValidator.validatedManually', { validatedAt: formattedDate })
            : t('fiscalInformationValidator.validatedSuccessfully', { validatedAt: formattedDate })}
        </Regular>
      </Flex>
      <Button variant={buttonStatus} onClick={() => {}} type="button" disabled={true}>
        <IconWrapper>
          <Check size={16} />
        </IconWrapper>
        {isValidatedManually
          ? t('fiscalInformationValidator.manuallySuccess')
          : t('fiscalInformationValidator.success')}
      </Button>
    </Container>
  );
};
